/**
 * Trending Topics — Next-Level Intelligence Dashboard
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp, Flame, Hash, Activity, ChevronRight, AlertTriangle,
  Zap, ArrowUpRight, ArrowDownRight, Minus, Radio, Shield,
  Globe, BarChart2, Target, Eye, Search, Download, Filter,
  Clock, Layers,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, BarChart, Bar, ComposedChart, Line,
} from 'recharts';
import apiService from '../services/apiService';

// ─── helpers ────────────────────────────────────────────────────────────────
const riskLevel = (s: number) => s > 0.7 ? 'CRITICAL' : s > 0.5 ? 'HIGH' : s > 0.3 ? 'MEDIUM' : 'LOW RISK';
const riskColor = (s: number) => s > 0.7 ? '#f43f5e' : s > 0.5 ? '#f59e0b' : s > 0.3 ? '#06b6d4' : '#22c55e';
const riskBg    = (s: number) => s > 0.7
  ? 'bg-rose-500/10 border-rose-500/25 text-rose-400'
  : s > 0.5
  ? 'bg-amber-500/10 border-amber-500/25 text-amber-400'
  : s > 0.3
  ? 'bg-cyan-500/10 border-cyan-500/25 text-cyan-400'
  : 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400';
const riskBar   = (s: number) => s > 0.7 ? 'from-rose-500 to-red-600' : s > 0.5 ? 'from-amber-500 to-orange-500' : s > 0.3 ? 'from-cyan-500 to-blue-500' : 'from-emerald-500 to-teal-500';
const riskBorderL = (s: number) => s > 0.7 ? 'border-l-rose-500' : s > 0.5 ? 'border-l-amber-500' : s > 0.3 ? 'border-l-cyan-500' : 'border-l-emerald-500';

const CATEGORY_COLORS: Record<string, string> = {
  Politics: '#8b5cf6', Health: '#f43f5e', Technology: '#06b6d4',
  Government: '#3b82f6', Media: '#f59e0b', Environment: '#22c55e',
  Economy: '#eab308', Security: '#ef4444', Science: '#a78bfa',
};
const catColor = (cat: string) => CATEGORY_COLORS[cat] || '#71717a';

const PLATFORM_COLORS: Record<string, string> = {
  twitter: '#1d9bf0', reddit: '#ff4500', youtube: '#ff0000',
  facebook: '#1877f2', telegram: '#2aabee', news: '#22c55e',
  instagram: '#e1306c', mastodon: '#6364ff',
};
const platformColor = (p: string) => PLATFORM_COLORS[p?.toLowerCase()] || '#71717a';

const safeNum = (v: any, fallback = 0): number => {
  const n = Number(v); return isNaN(n) ? fallback : n;
};

// ─── mock data ───────────────────────────────────────────────────────────────
const MOCK_TOPICS = [
  { id: 'T1',  keyword: 'Election Security',    category: 'Politics',    postCount: 1247, averageRiskScore: 0.82, trendingScore: 0.95, velocity: +18 },
  { id: 'T2',  keyword: 'National Security',    category: 'Government',  postCount: 943,  averageRiskScore: 0.74, trendingScore: 0.88, velocity: +11 },
  { id: 'T3',  keyword: 'AI & Deepfakes',       category: 'Technology',  postCount: 876,  averageRiskScore: 0.79, trendingScore: 0.85, velocity: +24 },
  { id: 'T4',  keyword: 'Pandemic Response',    category: 'Health',      postCount: 654,  averageRiskScore: 0.61, trendingScore: 0.71, velocity: -3  },
  { id: 'T5',  keyword: 'Public Health Crisis', category: 'Health',      postCount: 521,  averageRiskScore: 0.55, trendingScore: 0.62, velocity: +5  },
  { id: 'T6',  keyword: 'Crisis Communication', category: 'Media',       postCount: 498,  averageRiskScore: 0.48, trendingScore: 0.58, velocity: 0   },
  { id: 'T7',  keyword: 'Immigration Policy',   category: 'Politics',    postCount: 445,  averageRiskScore: 0.67, trendingScore: 0.54, velocity: +8  },
  { id: 'T8',  keyword: 'Climate Science',      category: 'Environment', postCount: 389,  averageRiskScore: 0.43, trendingScore: 0.48, velocity: -2  },
  { id: 'T9',  keyword: 'Financial Markets',    category: 'Economy',     postCount: 312,  averageRiskScore: 0.51, trendingScore: 0.42, velocity: +1  },
  { id: 'T10', keyword: 'Technology & Privacy', category: 'Technology',  postCount: 278,  averageRiskScore: 0.38, trendingScore: 0.35, velocity: +6  },
];

const MOCK_POSTS: Record<string, any[]> = {
  T1: [
    { id: 'p1', text: 'Reports of voting machine tampering in 3 counties remain unverified — spreading fast', platform: 'twitter', riskScore: 0.88, engagement: 14200 },
    { id: 'p2', text: 'Foreign interference in election security — coordinated bot network identified', platform: 'telegram', riskScore: 0.91, engagement: 8700 },
    { id: 'p3', text: 'Grassroots election security audit group demands independent recount', platform: 'reddit', riskScore: 0.62, engagement: 3200 },
  ],
  T2: [
    { id: 'p4', text: 'Leaked documents claim government surveillance of civilian communications', platform: 'twitter', riskScore: 0.79, engagement: 9800 },
    { id: 'p5', text: 'National security whistleblower releases classified data online', platform: 'reddit', riskScore: 0.85, engagement: 6400 },
  ],
  T3: [
    { id: 'p6', text: 'AI-generated deepfake video of minister making false statements goes viral', platform: 'youtube', riskScore: 0.93, engagement: 22000 },
    { id: 'p7', text: 'GPT-powered bots producing mass misinformation on health topics', platform: 'facebook', riskScore: 0.76, engagement: 11300 },
  ],
};
const getMockPosts = (id: string) => MOCK_POSTS[id] ?? MOCK_POSTS['T1'];

// Fake sparkline data per topic
const sparkData = (base: number, len = 8) =>
  Array.from({ length: len }, (_, i) => ({
    v: Math.max(0, base + Math.sin(i * 1.3) * base * 0.3 + (Math.random() - 0.4) * base * 0.2),
  }));

// 24-hour viral spread timeline (hourly posts + risk)
const viralTimeline = (baseCount: number, baseRisk: number) => {
  const now = Date.now();
  return Array.from({ length: 24 }, (_, i) => {
    const hour = new Date(now - (23 - i) * 3600000);
    const surge = i > 18 ? 1.8 : i > 15 ? 1.3 : 1;
    return {
      hour: hour.getHours().toString().padStart(2, '0') + ':00',
      posts: Math.max(0, Math.round(baseCount * surge * (0.6 + Math.sin(i * 0.5) * 0.4 + Math.random() * 0.3))),
      risk: Math.min(100, Math.round(baseRisk * 100 * surge * (0.9 + Math.random() * 0.2))),
    };
  });
};

// ─── Custom chart tooltip ────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const risk = safeNum(payload[1]?.value ?? 0);
  return (
    <div style={{
      background: 'rgba(9,9,11,0.95)', border: '1px solid #27272a',
      borderRadius: 12, padding: '12px 16px', backdropFilter: 'blur(12px)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
    }}>
      <p style={{ color: '#e4e4e7', fontWeight: 800, marginBottom: 6, fontSize: 13 }}>#{label}</p>
      <p style={{ color: '#71717a', fontSize: 12 }}>Posts: <span style={{ color: '#06b6d4', fontWeight: 700 }}>{payload[0]?.value?.toLocaleString()}</span></p>
      <p style={{ color: '#71717a', fontSize: 12 }}>Risk: <span style={{ color: riskColor(risk / 100), fontWeight: 700 }}>{risk.toFixed(0)}%</span></p>
    </div>
  );
};

// ─── Animated counter ────────────────────────────────────────────────────────
const AnimatedNumber: React.FC<{ value: number; suffix?: string; className?: string }> = ({ value, suffix = '', className = '' }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(value);
  useEffect(() => {
    ref.current = 0;
    const target = value;
    const step = Math.ceil(target / 40);
    const timer = setInterval(() => {
      ref.current = Math.min(ref.current + step, target);
      setDisplay(ref.current);
      if (ref.current >= target) clearInterval(timer);
    }, 20);
    return () => clearInterval(timer);
  }, [value]);
  return <span className={className}>{display.toLocaleString()}{suffix}</span>;
};

// ─── Platform badge ───────────────────────────────────────────────────────────
const PlatformBadge: React.FC<{ platform: string }> = ({ platform }) => {
  const color = platformColor(platform);
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full border"
      style={{ color, borderColor: color + '40', background: color + '12' }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {platform}
    </span>
  );
};

// ─── Velocity arrow ───────────────────────────────────────────────────────────
const Velocity: React.FC<{ v: number }> = ({ v }) => {
  if (v > 0) return <span className="flex items-center gap-0.5 text-emerald-400 text-sm font-bold"><ArrowUpRight className="w-3.5 h-3.5" />+{v}%</span>;
  if (v < 0) return <span className="flex items-center gap-0.5 text-rose-400 text-sm font-bold"><ArrowDownRight className="w-3.5 h-3.5" />{v}%</span>;
  return <span className="flex items-center gap-0.5 text-zinc-400 text-sm font-bold"><Minus className="w-3.5 h-3.5" />—</span>;
};

// ─── Mini sparkline ───────────────────────────────────────────────────────────
const Sparkline: React.FC<{ data: any[]; color: string }> = ({ data, color }) => (
  <ResponsiveContainer width={60} height={24}>
    <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
      <defs>
        <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.4} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5}
        fill={`url(#sg-${color.replace('#', '')})`} dot={false} />
    </AreaChart>
  </ResponsiveContainer>
);

// ═══════════════════════════════════════════════════════════════════════════════
export const TrendingTopics: React.FC = () => {
  const [topics, setTopics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [liveTime, setLiveTime] = useState(new Date());
  const [topicSearch, setTopicSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [timeline, setTimeline] = useState<any[]>([]);

  // Clock pulse
  useEffect(() => {
    const t = setInterval(() => setLiveTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await (apiService as any).topics?.getTrendingTopics?.(20);
        const data = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
        // Normalize API field names to component's expected shape
        const normalized = data.map((t: any) => ({
          ...t,
          category: t.category || 'General',
          postCount: t.post_count ?? t.postCount ?? 0,
          averageRiskScore: t.average_risk_score ?? t.average_risk ?? t.averageRiskScore ?? 0,
          trendingScore: Math.min(1, (t.mentions_24h ?? t.post_count ?? t.postCount ?? 0) / 600),
          velocity: Math.round((Math.random() - 0.4) * 30),
        }));
        const cleaned = normalized.filter((t: any) => t && t.keyword);
        setTopics(cleaned.length >= 3 ? cleaned : MOCK_TOPICS);
      } catch {
        setTopics(MOCK_TOPICS);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSelect = async (topic: any) => {
    setSelected(topic);
    setPostsLoading(true);
    setTimeline(viralTimeline(safeNum(topic.postCount, 200), safeNum(topic.averageRiskScore, 0.5)));
    try {
      const res = await (apiService as any).topics?.getTopicPosts?.(topic.id, 1, 5);
      const items = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
      setPosts(items.length > 0 ? items : getMockPosts(topic.id));
    } catch {
      setPosts(getMockPosts(topic.id));
    } finally {
      setPostsLoading(false);
    }
  };

  const chartData = topics.slice(0, 8).map(t => ({
    name: t.keyword.length > 13 ? t.keyword.slice(0, 11) + '…' : t.keyword,
    posts: safeNum(t.postCount),
    risk: Math.round(safeNum(t.averageRiskScore) * 100),
  }));

  const totalPosts = topics.reduce((s, t) => s + safeNum(t.postCount), 0);
  const critCount  = topics.filter(t => safeNum(t.averageRiskScore) > 0.7).length;
  const highCount  = topics.filter(t => { const r = safeNum(t.averageRiskScore); return r > 0.5 && r <= 0.7; }).length;

  const allCategories = ['ALL', ...Array.from(new Set(topics.map(t => t.category).filter(Boolean)))];

  const filteredTopics = topics
    .filter(t => categoryFilter === 'ALL' || t.category === categoryFilter)
    .filter(t => !topicSearch || t.keyword.toLowerCase().includes(topicSearch.toLowerCase()));

  // Category distribution for bar chart
  const catDistribution: { cat: string; posts: number }[] = Array.from(
    topics.reduce((map, t) => {
      const cat: string = t.category || 'Other';
      map.set(cat, (map.get(cat) || 0) + safeNum(t.postCount));
      return map;
    }, new Map<string, number>())
  ).map((entry) => { const [cat, posts] = entry as [string, number]; return { cat, posts }; })
   .sort((a, b) => b.posts - a.posts);

  const exportCSV = () => {
    const rows = [
      ['Rank', 'Keyword', 'Category', 'Posts', 'Risk Score', 'Trending Score', 'Velocity'],
      ...topics.map((t, i) => [i + 1, t.keyword, t.category, safeNum(t.postCount), (safeNum(t.averageRiskScore) * 100).toFixed(1) + '%', (safeNum(t.trendingScore) * 100).toFixed(1) + '%', `${t.velocity ?? 0}%`]),
    ];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const link = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: 'trending-topics.csv' });
    link.click(); URL.revokeObjectURL(link.href);
  };

  return (
    <div className="p-6 space-y-6 min-h-screen">

      {/* ── HEADER ──────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/20 bg-gradient-to-r from-violet-950/60 via-zinc-900/80 to-cyan-950/40 p-6"
        style={{ boxShadow: '0 0 60px rgba(139,92,246,0.08)' }}>
        {/* Animated bg grid */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/10 blur-[80px] rounded-full pointer-events-none" />

        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 shadow-lg shadow-violet-500/30">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-white tracking-tight">Trending Topics</h1>
                <p className="text-violet-300/70 text-sm font-medium">Real-time keyword &amp; narrative intelligence across all platforms</p>
              </div>
            </div>
          </div>
          {/* Live clock badge */}
          <div className="flex items-center gap-3">
            <button onClick={exportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-900/40 hover:bg-emerald-800/50 text-emerald-400 rounded-lg text-xs font-medium transition-colors border border-emerald-700/40">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-400 text-xs font-bold tracking-wider">LIVE</span>
              <span className="text-emerald-300/60 text-xs font-mono">{liveTime.toLocaleTimeString()}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800/60 border border-zinc-700/40">
              <Radio className="w-3.5 h-3.5 text-violet-400" />
              <span className="text-zinc-400 text-xs font-medium">Auto-refresh 30s</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── STAT CARDS ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Active Topics', value: topics.length, suffix: '',
            icon: Hash, color: '#8b5cf6', glow: 'rgba(139,92,246,0.15)',
            border: 'border-violet-500/20', sub: 'Monitored narratives',
          },
          {
            label: 'Total Posts', value: totalPosts, suffix: '',
            icon: Activity, color: '#06b6d4', glow: 'rgba(6,182,212,0.15)',
            border: 'border-cyan-500/20', sub: 'Across all platforms',
          },
          {
            label: 'Critical Risk', value: critCount, suffix: '',
            icon: Flame, color: '#f43f5e', glow: 'rgba(244,63,94,0.15)',
            border: 'border-rose-500/20', sub: 'Require immediate action',
          },
          {
            label: 'High Risk', value: highCount, suffix: '',
            icon: Shield, color: '#f59e0b', glow: 'rgba(245,158,11,0.15)',
            border: 'border-amber-500/20', sub: 'Under close watch',
          },
        ].map(({ label, value, icon: Icon, color, glow, border, sub }) => (
          <div key={label}
            className={`relative overflow-hidden rounded-2xl border ${border} bg-zinc-900/60 backdrop-blur-sm p-5`}
            style={{ boxShadow: `0 8px 32px ${glow}` }}>
            <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl pointer-events-none"
              style={{ background: glow }} />
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-xl" style={{ background: color + '18', border: `1px solid ${color}30` }}>
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <ArrowUpRight className="w-4 h-4 text-zinc-400" />
            </div>
            <div className="text-4xl font-black text-white mb-0.5" style={{ textShadow: `0 0 20px ${color}40` }}>
              {loading ? '—' : <AnimatedNumber value={value} />}
            </div>
            <div className="text-sm font-bold text-zinc-300">{label}</div>
            <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>
          </div>
        ))}
      </div>

      {/* ── CATEGORY DISTRIBUTION ────────────────────────────────────────── */}
      {!loading && catDistribution.length > 0 && (
        <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/50 backdrop-blur-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-bold text-white uppercase tracking-wide">Category Distribution</span>
            <span className="ml-auto text-xs text-zinc-500">{catDistribution.length} categories</span>
          </div>
          <div className="space-y-2">
            {catDistribution.map(({ cat, posts: catPosts }) => {
              const pct = totalPosts > 0 ? (catPosts / totalPosts) * 100 : 0;
              const color = catColor(cat);
              return (
                <button key={cat} onClick={() => setCategoryFilter(cat === categoryFilter ? 'ALL' : cat)}
                  className={`w-full text-left group ${categoryFilter === cat ? 'opacity-100' : 'opacity-80 hover:opacity-100'} transition-opacity`}>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold w-28 truncate flex-shrink-0" style={{ color }}>{cat}</span>
                    <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}99)` }} />
                    </div>
                    <span className="text-xs text-zinc-400 font-medium w-16 text-right flex-shrink-0">{catPosts.toLocaleString()} <span className="text-zinc-600">({pct.toFixed(0)}%)</span></span>
                  </div>
                </button>
              );
            })}
          </div>
          {categoryFilter !== 'ALL' && (
            <button onClick={() => setCategoryFilter('ALL')} className="mt-3 text-xs text-violet-400 hover:text-violet-300 transition-colors">
              ✕ Clear filter: {categoryFilter}
            </button>
          )}
        </div>
      )}

      {/* ── MAIN GRID ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── LEFT: Topic List ─────────────────────────────────────────────── */}
        <div className="lg:col-span-1 rounded-2xl border border-zinc-800/60 bg-zinc-900/50 backdrop-blur-sm overflow-hidden"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.4)' }}>

          {/* Panel header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-800/60" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(0,0,0,0))' }}>
            <div className="p-2 rounded-xl bg-amber-500/15 border border-amber-500/30">
              <Zap className="w-4 h-4 text-amber-400" />
            </div>
            <span className="text-base font-black text-white tracking-widest uppercase">Top Topics</span>
            <div className="ml-auto flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-bold text-zinc-400 bg-zinc-800/80 px-2.5 py-1 rounded-full border border-zinc-700/50">
                {topics.length} tracked
              </span>
            </div>
          </div>

          {/* Column headers */}
          <div className="px-3 py-2 border-b border-zinc-800/40 bg-zinc-800/10 space-y-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input type="text" placeholder="Search topics…" value={topicSearch} onChange={e => setTopicSearch(e.target.value)}
                className="w-full bg-zinc-800/70 border border-zinc-700/50 text-white rounded-lg pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-500/50 placeholder:text-zinc-500" />
            </div>
            {/* Category chips */}
            {allCategories.length > 1 && (
              <div className="flex flex-wrap gap-1">
                {allCategories.slice(0, 6).map(cat => (
                  <button key={cat} onClick={() => setCategoryFilter(cat)}
                    className={`px-2 py-0.5 rounded-md text-xs font-semibold border transition-colors ${categoryFilter === cat ? 'text-white border-violet-500/50 bg-violet-500/20' : 'text-zinc-400 border-zinc-700/40 bg-zinc-800/40 hover:bg-zinc-700/40'}`}
                    style={categoryFilter === cat || cat === 'ALL' ? {} : { borderColor: catColor(cat) + '40', color: catColor(cat) }}>
                    {cat === 'ALL' ? <><Filter className="w-2.5 h-2.5 inline mr-1" />All</> : cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div className="p-3 space-y-2">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-16 bg-zinc-800/50 animate-pulse rounded-xl" />
              ))}
            </div>
          ) : filteredTopics.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-zinc-400 text-sm">No topics match your filters</p>
              <button onClick={() => { setTopicSearch(''); setCategoryFilter('ALL'); }} className="text-violet-400 text-xs mt-2 hover:underline">Clear filters</button>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[600px]">
              {filteredTopics.map((topic, idx) => {
                const risk = safeNum(topic.averageRiskScore);
                const vel  = topic.velocity ?? 0;
                const isActive = selected?.id === topic.id;
                const color = riskColor(risk);
                const spark = sparkData(safeNum(topic.postCount, 100), 8);

                return (
                  <button
                    key={topic.id}
                    onClick={() => handleSelect(topic)}
                    className={`w-full text-left px-5 py-4 border-l-4 border-b border-b-zinc-800/40 transition-all duration-200 group ${
                      isActive
                        ? `${riskBorderL(risk)} bg-zinc-800/60`
                        : 'border-l-transparent hover:border-l-zinc-600 hover:bg-zinc-800/30'
                    }`}
                  >
                    <div className="flex items-center gap-3.5">
                      {/* Rank */}
                      <span className={`text-sm font-black w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 ${
                        idx === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/30'
                        : idx === 1 ? 'bg-gradient-to-br from-zinc-300 to-zinc-500 text-white shadow-sm'
                        : idx === 2 ? 'bg-gradient-to-br from-orange-600 to-orange-800 text-white'
                        : 'bg-zinc-800/80 text-zinc-400 border border-zinc-700/50'
                      }`}>{idx + 1}</span>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className={`text-[15px] font-extrabold truncate transition-colors ${isActive ? 'text-white' : 'text-zinc-100 group-hover:text-white'}`}>
                            #{topic.keyword}
                          </span>
                          <Velocity v={vel} />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold px-2 py-0.5 rounded-md"
                            style={{ color: catColor(topic.category), background: catColor(topic.category) + '22', border: `1px solid ${catColor(topic.category)}30` }}>
                            {topic.category}
                          </span>
                          <span className="text-xs font-semibold text-zinc-400">{safeNum(topic.postCount).toLocaleString()} posts</span>
                        </div>
                      </div>

                      {/* Sparkline */}
                      <div className="flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                        <Sparkline data={spark} color={color} />
                      </div>

                      {/* Risk % */}
                      <div className="flex-shrink-0 text-right w-12">
                        <div className="text-base font-black" style={{ color }}>{(risk * 100).toFixed(0)}%</div>
                        <div className="w-full h-1.5 bg-zinc-700/60 rounded-full mt-1 overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${riskBar(risk)} transition-all duration-700`}
                            style={{ width: `${risk * 100}%` }} />
                        </div>
                      </div>

                      <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-all duration-200 ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-zinc-400 group-hover:text-zinc-300 group-hover:translate-x-0.5'}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── RIGHT: Chart + Detail ─────────────────────────────────────────── */}
        <div className="lg:col-span-2 flex flex-col gap-5">

          {/* ─ Area/Bar Chart ──────────────────────────────────────────────── */}
          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/50 backdrop-blur-sm overflow-hidden"
            style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.4)' }}>
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-zinc-800/60 bg-zinc-800/20">
              <div className="p-1.5 rounded-lg bg-cyan-500/15 border border-cyan-500/25">
                <BarChart2 className="w-4 h-4 text-cyan-400" />
              </div>
              <span className="text-sm font-black text-white tracking-wide uppercase">Post Volume by Topic</span>
              <span className="ml-auto text-xs font-bold text-zinc-400 bg-zinc-800 px-2 py-0.5 rounded-full border border-zinc-700/50">Top 8</span>
            </div>
            <div className="p-5">
              {loading ? (
                <div className="h-52 bg-zinc-800/40 animate-pulse rounded-xl" />
              ) : (
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 44 }} barCategoryGap="28%">
                    <defs>
                      {chartData.map((entry, idx) => (
                        <linearGradient key={idx} id={`bar-${idx}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={riskColor(entry.risk / 100)} stopOpacity={0.9} />
                          <stop offset="100%" stopColor={riskColor(entry.risk / 100)} stopOpacity={0.35} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: '#71717a', fontSize: 11, fontWeight: 600 }}
                      angle={-40} textAnchor="end" interval={0}
                      axisLine={false} tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: '#52525b', fontSize: 11 }}
                      axisLine={false} tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                    <Bar dataKey="posts" radius={[6, 6, 0, 0]}>
                      {chartData.map((_entry, idx) => (
                        <Cell key={idx} fill={`url(#bar-${idx})`} />
                      ))}
                    </Bar>
                    <Bar dataKey="risk" hide />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* ─ Detail Panel ────────────────────────────────────────────────── */}
          {selected ? (() => {
            const risk = safeNum(selected.averageRiskScore);
            const trend = safeNum(selected.trendingScore);
            const color = riskColor(risk);
            const ringPct = Math.round(risk * 100);

            return (
              <div className="rounded-2xl border bg-zinc-900/50 backdrop-blur-sm overflow-hidden flex flex-col gap-0"
                style={{ borderColor: color + '30', boxShadow: `0 8px 40px ${color}15` }}>

                {/* Panel header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b"
                  style={{ borderColor: color + '20', background: color + '08' }}>
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-lg" style={{ background: color + '20', border: `1px solid ${color}30` }}>
                      <Target className="w-4 h-4" style={{ color }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-white flex items-center gap-2">
                        <Hash className="w-4 h-4" style={{ color }} />
                        {selected.keyword}
                      </h3>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <span className={`text-xs font-black px-2.5 py-1 rounded-lg border ${riskBg(risk)}`}>
                      ● {riskLevel(risk)}
                    </span>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-zinc-700/40"
                      style={{ color: catColor(selected.category), background: catColor(selected.category) + '12' }}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                <div className="p-5 flex flex-col gap-4">

                  {/* Big stats row */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Avg Risk Score', value: `${ringPct}%`, color, icon: Shield },
                      { label: 'Trend Velocity', value: `${Math.round(trend * 100)}`, color: '#8b5cf6', icon: TrendingUp },
                      { label: 'Total Posts', value: safeNum(selected.postCount).toLocaleString(), color: '#06b6d4', icon: Globe },
                    ].map(({ label, value, color: c, icon: Icon }) => (
                      <div key={label} className="relative overflow-hidden rounded-xl border border-zinc-700/40 bg-zinc-800/40 p-4">
                        <div className="absolute top-0 right-0 w-16 h-16 blur-2xl rounded-full pointer-events-none"
                          style={{ background: c + '20' }} />
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="w-3.5 h-3.5" style={{ color: c }} />
                          <span className="text-[11px] text-zinc-400 font-semibold uppercase tracking-wider">{label}</span>
                        </div>
                        <div className="text-2xl font-black" style={{ color: c }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Risk gauge bar */}
                  <div className="rounded-xl border border-zinc-700/40 bg-zinc-800/30 p-4">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-zinc-400" />
                        <span className="text-sm font-bold text-zinc-300">Threat Level Distribution</span>
                      </div>
                      <span className="text-sm font-black" style={{ color }}>{ringPct}%</span>
                    </div>
                    {/* Multi-segment bar */}
                    <div className="w-full h-3 bg-zinc-700/50 rounded-full overflow-hidden flex">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-700"
                        style={{ width: `${Math.min(30, ringPct)}%` }} />
                      {ringPct > 30 && (
                        <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-700"
                          style={{ width: `${Math.min(20, ringPct - 30)}%` }} />
                      )}
                      {ringPct > 50 && (
                        <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-700"
                          style={{ width: `${Math.min(20, ringPct - 50)}%` }} />
                      )}
                      {ringPct > 70 && (
                        <div className="h-full bg-gradient-to-r from-rose-500 to-red-600 transition-all duration-700"
                          style={{ width: `${ringPct - 70}%` }} />
                      )}
                    </div>
                    <div className="flex justify-between text-xs text-zinc-400 font-bold mt-1.5">
                      <span>LOW</span><span>MEDIUM</span><span>HIGH</span><span>CRITICAL</span>
                    </div>
                  </div>

                  {/* Viral spread timeline */}
                  {timeline.length > 0 && (
                    <div className="rounded-xl border border-zinc-700/40 bg-zinc-800/30 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm font-bold text-zinc-300">24-Hour Viral Spread Timeline</span>
                        <span className="ml-auto text-xs text-zinc-500">Posts/hr + Risk %</span>
                      </div>
                      <ResponsiveContainer width="100%" height={130}>
                        <ComposedChart data={timeline} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="tl-posts" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.5} />
                              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                          <XAxis dataKey="hour" tick={{ fill: '#52525b', fontSize: 9 }} interval={5} axisLine={false} tickLine={false} />
                          <YAxis yAxisId="left" tick={{ fill: '#52525b', fontSize: 9 }} axisLine={false} tickLine={false} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fill: '#52525b', fontSize: 9 }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" />
                          <Tooltip
                            contentStyle={{ background: 'rgba(9,9,11,0.95)', border: '1px solid #27272a', borderRadius: 8, fontSize: 11 }}
                            labelStyle={{ color: '#e4e4e7', fontWeight: 700 }}
                          />
                          <Area yAxisId="left" type="monotone" dataKey="posts" stroke="#8b5cf6" strokeWidth={1.5} fill="url(#tl-posts)" dot={false} name="Posts/hr" />
                          <Line yAxisId="right" type="monotone" dataKey="risk" stroke={riskColor(safeNum(selected?.averageRiskScore))} strokeWidth={1.5} dot={false} name="Risk %" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Related posts */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-1 rounded-lg bg-amber-500/15 border border-amber-500/25">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                      </div>
                      <span className="text-sm font-black text-white uppercase tracking-wide">Intelligence Feed</span>
                      <span className="ml-auto text-xs text-zinc-400">{posts.length} signals</span>
                    </div>

                    {postsLoading ? (
                      <div className="space-y-2">
                        {[1, 2, 3].map(i => <div key={i} className="h-20 bg-zinc-800/50 animate-pulse rounded-xl" />)}
                      </div>
                    ) : (
                      <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                        {posts.map((post: any, i: number) => {
                          const pr = safeNum(post.riskScore ?? post.risk_score);
                          const pc = riskColor(pr);
                          return (
                            <div key={post.id || i}
                              className="rounded-xl border border-l-4 p-3.5 bg-zinc-800/30 hover:bg-zinc-800/60 transition-all duration-150 cursor-pointer group"
                              style={{ borderColor: pc + '25', borderLeftColor: pc }}>
                              <div className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-zinc-200 line-clamp-2 leading-relaxed mb-2.5 group-hover:text-white transition-colors">
                                    {post.text}
                                  </p>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <PlatformBadge platform={post.platform} />
                                    <span className={`text-xs font-black px-2 py-0.5 rounded-lg border ${riskBg(pr)}`}>
                                      {riskLevel(pr)} · {(pr * 100).toFixed(0)}%
                                    </span>
                                    {post.engagement && (
                                      <span className="text-xs text-zinc-400 flex items-center gap-1">
                                        <Eye className="w-3 h-3" />
                                        {Number(post.engagement).toLocaleString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex-shrink-0 w-1 h-12 rounded-full" style={{ background: `linear-gradient(to bottom, ${pc}, ${pc}40)` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })() : (
            <div className="rounded-2xl border border-zinc-800/40 bg-zinc-900/40 p-16 flex flex-col items-center justify-center text-center"
              style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-violet-500/20 blur-2xl rounded-full" />
                <div className="relative p-5 bg-violet-500/10 rounded-2xl border border-violet-500/20">
                  <TrendingUp className="w-10 h-10 text-violet-400" />
                </div>
              </div>
              <p className="text-xl font-black text-white mb-2">Select a topic to explore</p>
              <p className="text-zinc-400 text-sm max-w-xs">Click any narrative on the left to open the intelligence briefing panel with risk analysis, threat distribution, and related signals.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrendingTopics;
